import PropTypes from "prop-types";
import React from "react";

import { Button, Text } from "@swa-ui/core";
import { Currency } from "@swa-ui/currency";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import * as flightStatusType from "../defines/flightStatusType";
import styles from "./FareOption.module.scss";

const DEFAULT_DISABLED_FARE_OPTION_I18N_KEY = "FareOption__UNAVAILABLE_LABEL";

/**
 * A component that displays a selectable fare option with pricing information.
 * including:
 * - The main fare amount.
 * - taxes and fees for a points purchase.
 * - Optional number of seats left at this fare.
 * - Optional points accrual information for a dollar purchase (shown on hover).
 */
export const FareOption = (props) => {
  const {
    accrualPoints,
    availabilityStatus,
    amount,
    className,
    currencyCode,
    onOptionSelect,
    seatsLeft,
    selected,
    taxesAndFees,
  } = props;
  const disabledFareOption = availabilityStatus !== flightStatusType.AVAILABLE;

  return <Button {...getButtonProps()}>{renderFareOption()}</Button>;

  function renderFareOption() {
    return disabledFareOption ? (
      <Text color="var(--fare-option-unavailable-text-color)">
        {i18n(getDisabledFareOptionI18nKey())}
      </Text>
    ) : (
      <div className={styles.fareOptionDetails}>
        <Currency {...getFareAmountProps()} />
        {currencyCode === "POINTS" ? renderPoints() : renderDollars()}
      </div>
    );
  }

  function renderPoints() {
    return (
      <>
        {renderTaxesAndFees()}
        {renderSeatsLeft()}
      </>
    );
  }

  function renderDollars() {
    return (
      <>
        {renderSeatsLeft()}
        {renderPointsAccrualOnHover()}
      </>
    );
  }

  function renderTaxesAndFees() {
    return <Currency {...getTaxesAndFeesProps()} />;
  }

  function renderSeatsLeft() {
    return (
      <Text {...getSeatsLeftProps()}>
        {i18n("FareOption__SEATS_LEFT_LABEL", { seatsLeft: seatsLeft ?? "0" })}
      </Text>
    );
  }

  function renderPointsAccrualOnHover() {
    return (
      <div
        className={classNames(styles.accrual, {
          [styles.hidden]: !accrualPoints,
        })}
      >
        {accrualPoints && (
          <>
            <Text size="small">{i18n("FareOption__ACCRUAL_LABEL")}</Text>
            <Currency {...getPointsAccrualOnHoverProps()} />
          </>
        )}
      </div>
    );
  }

  function getButtonProps() {
    const styleType = selected ? "tertiary" : "secondary";

    return {
      className: classNames(className, styles.container, {
        [styles.activeContainer]: !disabledFareOption,
        [styles.selected]: selected,
        [styles.unavailableContainer]: shouldFareRenderAsUnavailable(),
      }),
      disabled: disabledFareOption,
      emphasis: false,
      onClick: onOptionSelect,
      size: "minimal",
      styleType,
    };
  }

  function getFareAmountProps() {
    const color = selected
      ? "var(--fare-option-selected-text-color)"
      : "var(--fare-option-amount-color)";

    return {
      amount,
      currencyCode,
      roundingType: parseFloat(amount) > 0 ? "up" : "down",
      superScriptSymbol: true,
      textProps: {
        bold: true,
        className: styles.currencyCentered,
        color,
        size: "xsmall",
        textType: "display",
      },
    };
  }

  function getTaxesAndFeesProps() {
    const { currencyCode: taxesAndFeesCurrencyCode = "USD", value = "0" } = taxesAndFees ?? {};
    const color = selected
      ? "var(--fare-option-selected-taxes-and-fees-color)"
      : "var(--fare-option-taxes-and-fees-color)";

    return {
      amount: value,
      currencyCode: taxesAndFeesCurrencyCode,
      showSignPrefix: true,
      superScriptSymbol: false,
      textProps: {
        className: classNames(styles.currencyCentered, { [styles.hidden]: !taxesAndFees }),
        color,
      },
    };
  }

  function getSeatsLeftProps() {
    return {
      className: classNames(styles.seatsLeftLabel, {
        [styles.hidden]: !seatsLeft,
      }),
      italics: true,
      size: "small",
    };
  }

  function getPointsAccrualOnHoverProps() {
    return {
      amount: accrualPoints,
      currencyCode: "POINTS",
      superScriptSymbol: false,
      textProps: {
        className: classNames(styles.currency, styles.accrualAmount),
        size: "small",
      },
    };
  }

  function getDisabledFareOptionI18nKey() {
    let key = DEFAULT_DISABLED_FARE_OPTION_I18N_KEY;

    if ([flightStatusType.ALL_SEATS_SOLD, flightStatusType.SOLD_OUT].includes(availabilityStatus)) {
      key = "FareOption__SOLD_OUT_LABEL";
    } else if (availabilityStatus === flightStatusType.INVALID_WITH_DEPART_DATE) {
      key = "FareOption__INVALID_WITH_DEPART_DATE_LABEL";
    } else if (availabilityStatus === flightStatusType.INVALID_WITH_RETURN_DATE) {
      key = "FareOption__INVALID_WITH_RETURN_DATE_LABEL";
    }

    return key;
  }

  function shouldFareRenderAsUnavailable() {
    return [
      flightStatusType.DEPARTED,
      flightStatusType.TOO_CLOSE_TO_DEPARTURE_DOMESTIC,
      flightStatusType.TOO_CLOSE_TO_DEPARTURE_INTERNATIONAL,
      flightStatusType.UNAVAILABLE,
    ].includes(availabilityStatus);
  }
};

FareOption.propTypes = {
  /** Points that will be accrued for this fare option */
  accrualPoints: PropTypes.string,

  /** The fare amount */
  amount: PropTypes.string,

  /** Field that describes the availability status of the flight. */
  availabilityStatus: PropTypes.oneOf(Object.values(flightStatusType)).isRequired,

  /** Additional class name to apply to the component */
  className: PropTypes.string,

  /** Currency code for the fare amount (e.g., 'USD') */
  currencyCode: PropTypes.string,

  /** Function called when the fare option is selected */
  onOptionSelect: PropTypes.func,

  /** Number of seats left at this fare price */
  seatsLeft: PropTypes.number,

  /** Indicates if this fare option is currently selected */
  selected: PropTypes.bool,

  /** Taxes and fees information for this fare */
  taxesAndFees: PropTypes.shape({
    currencyCode: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }),
};
